import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PlatformUpdate,
  PlatformUpdateInput,
  PlatformUpdateType,
  archivePlatformUpdate,
  createPlatformUpdate,
  getPlatformUpdateDrafts,
  publishPlatformUpdate,
  updatePlatformUpdate,
} from '../../api/platform/system';
import './SystemUpdateManager.css';

const UPDATE_TYPES: Array<{ value: PlatformUpdateType; label: string }> = [
  { value: 'update', label: 'Update' },
  { value: 'feature', label: 'Feature' },
  { value: 'bug_fix', label: 'Bug fix' },
  { value: 'release', label: 'Release' },
  { value: 'deployment', label: 'Deployment' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'incident', label: 'Incident' },
  { value: 'security', label: 'Security' },
];

const EMPTY_FORM: PlatformUpdateInput = {
  title: '',
  summary: '',
  type: 'update',
  version: '',
};

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'Something went wrong while managing the update.';

const SystemUpdateManager: React.FC<{ onPublished?: () => void }> = ({ onPublished }) => {
  const [drafts, setDrafts] = useState<PlatformUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<PlatformUpdateInput>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<PlatformUpdate['id'] | null>(null);
  const [saving, setSaving] = useState(false);
  const [workingId, setWorkingId] = useState<PlatformUpdate['id'] | null>(null);

  const loadDrafts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDrafts(await getPlatformUpdateDrafts());
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const heading = useMemo(() => (editingId == null ? 'Create update' : 'Edit draft'), [editingId]);

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const startEditing = (draft: PlatformUpdate) => {
    setEditingId(draft.id);
    setForm({
      title: draft.title || '',
      summary: draft.summary || '',
      type: (draft.type as PlatformUpdateType) || 'update',
      version: draft.version || '',
      pullRequestNumber: draft.pullRequestNumber || null,
      gitCommitSha: draft.gitCommitSha || null,
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title.trim()) return;

    setSaving(true);
    setError(null);
    try {
      if (editingId == null) {
        await createPlatformUpdate(form);
      } else {
        await updatePlatformUpdate(editingId, form);
      }
      resetForm();
      await loadDrafts();
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (draft: PlatformUpdate) => {
    setWorkingId(draft.id);
    setError(null);
    try {
      await publishPlatformUpdate(draft.id);
      if (editingId === draft.id) resetForm();
      await loadDrafts();
      onPublished?.();
    } catch (publishError) {
      setError(getErrorMessage(publishError));
    } finally {
      setWorkingId(null);
    }
  };

  const handleArchive = async (draft: PlatformUpdate) => {
    setWorkingId(draft.id);
    setError(null);
    try {
      await archivePlatformUpdate(draft.id);
      if (editingId === draft.id) resetForm();
      await loadDrafts();
    } catch (archiveError) {
      setError(getErrorMessage(archiveError));
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <section className="system-card system-update-manager">
      <div className="system-card__header">
        <div>
          <span className="system-card__eyebrow">Release workflow</span>
          <h2>Update Drafts</h2>
        </div>
        <span className="system-card__count">{drafts.length}</span>
      </div>

      <div className="system-update-manager__layout">
        <div className="system-update-manager__drafts">
          {loading ? <div className="system-empty">Loading update drafts…</div> : null}
          {!loading && !drafts.length ? (
            <div className="system-empty">No drafts. Merged PRs will appear here when GitHub ingestion is configured.</div>
          ) : null}
          {drafts.map((draft) => (
            <div className="system-update-draft" key={draft.id}>
              <div className="system-update-draft__body">
                <div className="system-update__title-row">
                  <strong>{draft.title}</strong>
                  <span className="system-pill">{String(draft.type || 'update').replace(/_/g, ' ')}</span>
                </div>
                {draft.summary ? <p>{draft.summary}</p> : <p className="system-update-draft__muted">No summary yet.</p>}
                <div className="system-update__meta">
                  {draft.version ? <span>v{draft.version}</span> : null}
                  {draft.pullRequestNumber ? <span>PR #{draft.pullRequestNumber}</span> : null}
                  {draft.gitCommitSha ? <span>{draft.gitCommitSha.slice(0, 7)}</span> : null}
                </div>
              </div>
              <div className="system-update-draft__actions">
                <button type="button" onClick={() => startEditing(draft)} disabled={workingId === draft.id}>Edit</button>
                <button type="button" onClick={() => handleArchive(draft)} disabled={workingId === draft.id}>Archive</button>
                <button className="system-update-draft__publish" type="button" onClick={() => handlePublish(draft)} disabled={workingId === draft.id}>
                  {workingId === draft.id ? 'Working…' : 'Publish'}
                </button>
              </div>
            </div>
          ))}
        </div>

        <form className="system-update-editor" onSubmit={handleSubmit}>
          <div className="system-update-editor__heading">
            <strong>{heading}</strong>
            {editingId != null ? <button type="button" onClick={resetForm}>Cancel</button> : null}
          </div>

          <label>
            Title
            <input
              value={form.title}
              maxLength={180}
              onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
              placeholder="What changed?"
              required
            />
          </label>

          <label>
            Summary
            <textarea
              value={form.summary || ''}
              rows={5}
              onChange={(event) => setForm((current) => ({ ...current, summary: event.target.value }))}
              placeholder="A short user-friendly summary of the change."
            />
          </label>

          <div className="system-update-editor__row">
            <label>
              Type
              <select
                value={form.type || 'update'}
                onChange={(event) => setForm((current) => ({ ...current, type: event.target.value as PlatformUpdateType }))}
              >
                {UPDATE_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label>
              Version
              <input
                value={form.version || ''}
                maxLength={60}
                onChange={(event) => setForm((current) => ({ ...current, version: event.target.value }))}
                placeholder="Optional"
              />
            </label>
          </div>

          {editingId != null && (form.pullRequestNumber || form.gitCommitSha) ? (
            <div className="system-update-editor__source">
              {form.pullRequestNumber ? <span>PR #{form.pullRequestNumber}</span> : null}
              {form.gitCommitSha ? <span>{form.gitCommitSha.slice(0, 7)}</span> : null}
            </div>
          ) : null}

          {error ? <div className="system-update-editor__error">{error}</div> : null}

          <button className="system-update-editor__save" type="submit" disabled={saving || !form.title.trim()}>
            {saving ? 'Saving…' : editingId == null ? 'Create draft' : 'Save draft'}
          </button>
        </form>
      </div>
    </section>
  );
};

export default SystemUpdateManager;
